import MojitoCore from 'mojito/core';

import AbstractNotificationsService from './abstract-notifications-service';
import ApiRequestProvider from 'services/common/api-request/request-provider.js';

const logger = MojitoCore.logger.get('XtremePushService');

/**
 * Permission types that can be granted for xtremepush messaging.
 *
 * @readonly
 * @enum {string}
 * @memberof Mojito.Services.SportsContent.ContentNotifications.xtremePushService
 */
const PERMISSIONS = {
    /** User has not allowed or blocked notifications yet. */
    DEFAULT: 'default',
    /** User has already allowed notifications permissions. */
    GRANTED: 'granted',
    /** User has already blocked notifications permissions. */
    DENIED: 'denied',
    /** Push messaging is not available in user browser. */
    UNAVAILABLE: 'unavailable',
};

const NOTIFICATIONS_BASE_URL = '/notifications';

/**
 * XtremePushService config.
 *
 * @typedef XtremePushServiceConfig
 * @type {object}
 * @property {string} serviceUrl - URL that will be used to perform API calls.
 *
 * @memberof Mojito.Services.SportsContent.ContentNotifications
 */

/**
 * Class offering the possibility to interact with the external notifications service API and with Mojito
 * transactional API to subscribe/unsubscribe to event notifications.
 *
 * @class XtremePushService
 * @name xtremePushService
 * @extends Mojito.Services.SportsContent.ContentNotifications.AbstractNotificationsService
 * @memberof Mojito.Services.SportsContent.ContentNotifications
 */
class XtremePushService extends AbstractNotificationsService {
    /**
     * Configure service.
     *
     * @param {Mojito.Services.SportsContent.ContentNotifications.XtremePushServiceConfig} config - Service configuration object.
     * @returns {Mojito.Services.SportsContent.ContentNotifications.AbstractNotificationsService} Instance of the service.
     * @function Mojito.Services.SportsContent.ContentNotifications.xtremePushService#configure
     */
    configure(config) {
        const { serviceUrl } = config;
        this.requestFactory = new ApiRequestProvider(logger);
        this.apiUrl = `${serviceUrl}${NOTIFICATIONS_BASE_URL}`;
        return this;
    }

    /**
     * Activates xtremePush API by forcing user to accept a notifications prompt.
     *
     * @returns {Promise} Promise object. Resolves on prompt accepted, rejects othervice.
     * @function Mojito.Services.SportsContent.ContentNotifications.xtremePushService#activate
     */
    activate() {
        return new Promise((resolve, reject) => {
            window.xtremepush('push', 'prompt', {
                allowCallback: resolve,
                blockCallback: reject,
                dismissCallback: reject,
            });
        });
    }

    /**
     * Follow request event.
     *
     * @param {Mojito.Services.SportsContent.ContentNotifications.types.FollowEventInfo} payload - Follow event payload.
     * @returns {Promise} Promise object.
     * @function Mojito.Services.SportsContent.ContentNotifications.xtremePushService#followEvent
     */
    followEvent(payload) {
        return this.requestFactory
            .post(`${this.apiUrl}/subscribe`)
            .withCredentials()
            .send(this.getRequestPayload(payload));
    }

    /**
     * Unfollow request event.
     *
     * @param {Mojito.Services.SportsContent.ContentNotifications.types.FollowEventInfo} payload - Follow event payload.
     * @returns {Promise} Promise object.
     * @function Mojito.Services.SportsContent.ContentNotifications.xtremePushService#unfollowEvent
     */
    unfollowEvent(payload) {
        return this.requestFactory
            .post(`${this.apiUrl}/unsubscribe`)
            .withCredentials()
            .send(this.getRequestPayload(payload));
    }

    /**
     * Indicates if xtremepush service is available in user's browser.
     *
     * @returns {boolean} True if available, false otherwise.
     * @function Mojito.Services.SportsContent.ContentNotifications.xtremePushService#isAvailable
     */
    isAvailable() {
        return this.getPermissions() !== PERMISSIONS.UNAVAILABLE;
    }

    /**
     * Indicates if xtremepush service is activated in user's browser.
     * Typically, this is done manually in a prompt where user can grant permissions
     * to xtremepush for sending push notifications.
     *
     * @returns {boolean} True if activated, false otherwise.
     * @function Mojito.Services.SportsContent.ContentNotifications.xtremePushService#isActivated
     */
    isActivated() {
        return this.getPermissions() === PERMISSIONS.GRANTED;
    }

    getDeviceId() {
        const info = window.xtremepush('get', 'device_info') || {};
        return info.id;
    }

    getPermissions() {
        return window.xtremepush('push', 'permission');
    }

    getRequestPayload({ notificationsId }) {
        return {
            notificationsId,
            subscriberId: this.getDeviceId(),
        };
    }
}

export default new XtremePushService();
